import {
  FocusEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';

import { Input } from '@/components/Input';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';

import { cn } from '@/lib/utils';

import { useDebounce } from '@/hooks';

import { useAsyncAutocompleteQuery } from '@/queries';

export type AsyncAutoCompleteProps<TItem, TResponse> = {
  fetchUrl: string;
  queryKey: string | readonly unknown[];
  getOptionLabel: (item: TItem) => string;
  getOptionValue: (item: TItem) => string;
  transformResponse: (response: TResponse) => TItem[];
  selectedValue: TItem | null;
  onSelectedValueChange: (value: TItem | null) => void;
  label?: ReactNode;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  debounceMs?: number;
  error?: string;
  id?: string;
};

export function AsyncAutoComplete<TItem, TResponse>(
  props: AsyncAutoCompleteProps<TItem, TResponse>,
) {
  const { t } = useTranslation();

  const {
    fetchUrl,
    queryKey,
    getOptionLabel,
    getOptionValue,
    transformResponse,
    selectedValue,
    onSelectedValueChange,
    label,
    emptyMessage,
    disabled = false,
    className,
    debounceMs = 500,
    error,
    id,
  } = props;

  const resolvedEmptyMessage = emptyMessage ?? t('Nothing Found');

  const [open, setOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(
    undefined,
  );
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const onContainerRef = useCallback((element: HTMLDivElement | null) => {
    containerRef.current = element;
    setNode(element);
  }, []);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    updateWidth();

    const resizeObserver = new ResizeObserver(updateWidth);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const {
    value: searchValue,
    setValue: setSearchValue,
    debouncedValue: debouncedSearchValue,
  } = useDebounce('', debounceMs);

  useEffect(() => {
    if (!selectedValue) {
      setSearchValue('');
    }
  }, [selectedValue, setSearchValue]);

  const { data: fetchedData, isLoading } = useAsyncAutocompleteQuery<
    TResponse,
    TItem
  >({
    queryKey,
    fetchUrl,
    searchValue: debouncedSearchValue,
    transformResponse,
    enabled: open,
  });

  const items = useMemo(() => {
    if (!fetchedData) return [];

    return fetchedData.map((item) => ({
      value: getOptionValue(item),
      label: getOptionLabel(item),
      original: item,
    }));
  }, [fetchedData, getOptionLabel, getOptionValue]);

  const labels = useMemo(
    () =>
      items.reduce(
        (acc, item) => {
          acc[item.value] = item.label;
          return acc;
        },
        {} as Record<string, string>,
      ),
    [items],
  );

  const selectedValueString = useMemo(() => {
    if (!selectedValue) return '';
    return getOptionValue(selectedValue);
  }, [selectedValue, getOptionValue]);

  const reset = useCallback(() => {
    onSelectedValueChange(null);
    setSearchValue('');
  }, [onSelectedValueChange, setSearchValue]);

  const onInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (
        e.relatedTarget?.closest('[cmdk-list]') ||
        e.relatedTarget?.closest('[cmdk-group]') ||
        e.relatedTarget?.closest('[data-slot="popover-content"]')
      ) {
        return;
      }

      // An untouched input keeps the current selection — only text typed over it
      // and left unmatched clears the field.
      if (!searchValue && selectedValue) {
        return;
      }

      if (labels[selectedValueString] !== searchValue) {
        reset();
      }
    },
    [labels, selectedValue, selectedValueString, searchValue, reset],
  );

  const onSelectItem = useCallback(
    (inputValue: string) => {
      if (inputValue === selectedValueString) {
        reset();
      } else {
        const selectedItem = items.find((item) => item.value === inputValue);
        onSelectedValueChange(selectedItem?.original || null);
        setSearchValue(labels[inputValue] ?? '');
      }
      setOpen(false);
    },
    [
      selectedValueString,
      items,
      labels,
      onSelectedValueChange,
      setSearchValue,
      reset,
    ],
  );

  const displayValue = useMemo(() => {
    if (searchValue) return searchValue;
    if (!selectedValue) return '';
    return getOptionLabel(selectedValue);
  }, [searchValue, selectedValue, getOptionLabel]);

  const dialogContainer =
    node?.closest<HTMLElement>('[data-slot="dialog-content"]') ?? null;

  return (
    <div
      ref={onContainerRef}
      className={cn('relative h-10 w-full', className)}
    >
      <Popover
        open={open}
        onOpenChange={setOpen}
      >
        <Command
          shouldFilter={false}
          className="h-10 overflow-visible bg-transparent"
        >
          <PopoverAnchor>
            <Input
              id={id}
              label={label}
              isInvalid={!!error}
              isActive={open}
              value={displayValue}
              onChange={(e) => {
                const value = e.target.value;
                setSearchValue(value);
                if (value) {
                  setOpen(true);
                } else {
                  onSelectedValueChange(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setOpen(false);
                }
              }}
              onMouseDown={() => setOpen(true)}
              onFocus={() => setOpen(true)}
              onBlur={onInputBlur}
              disabled={disabled}
              className="h-10 focus-visible:ring-0 focus-visible:outline-none"
            />
          </PopoverAnchor>
          {!open && (
            <CommandList
              aria-hidden="true"
              className="hidden"
            />
          )}
          <PopoverContent
            container={dialogContainer}
            onOpenAutoFocus={(e) => e.preventDefault()}
            onCloseAutoFocus={(e) => e.preventDefault()}
            onInteractOutside={(e) => {
              if (containerRef.current?.contains(e.target as Node)) {
                e.preventDefault();
              }
            }}
            className="p-0"
            style={{ width: containerWidth }}
            align="start"
          >
            <CommandList>
              {isLoading && (
                <div className="p-1">
                  <Skeleton className="h-6 w-full" />
                </div>
              )}
              {items.length > 0 && !isLoading ? (
                <CommandGroup>
                  {items.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onMouseDown={(e) => e.preventDefault()}
                      onSelect={onSelectItem}
                    >
                      <Check
                        className={cn(
                          'mr-2 size-4',
                          selectedValueString === option.value
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ) : null}
              {!isLoading ? (
                <CommandEmpty>{resolvedEmptyMessage}</CommandEmpty>
              ) : null}
            </CommandList>
          </PopoverContent>
        </Command>
      </Popover>
      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  );
}

/**
 * Factory function to create a pre-configured AsyncAutoComplete component.
 *
 * This allows you to "bake in" the data transformation logic once and reuse
 * the component without passing the same props repeatedly.
 *
 * @param config - Configuration object with required transformation functions
 * @param config.getOptionLabel - Extracts display text from an item
 * @param config.getOptionValue - Extracts unique identifier from an item
 * @param config.transformResponse - Transforms API response into an array of items
 *
 * @example
 * const UserAutoComplete = createAsyncAutoComplete<User, UsersResponse>({
 *   getOptionLabel: (user) => user.name,
 *   getOptionValue: (user) => String(user.id),
 *   transformResponse: (response) => response.users,
 * });
 *
 * // Usage - no need to pass transformation props each time
 * <UserAutoComplete
 *   fetchUrl="/api/users"
 *   queryKey="users"
 *   selectedValue={selectedUser}
 *   onSelectedValueChange={setSelectedUser}
 * />
 */
export const createAsyncAutoComplete = <TItem, TResponse>(config: {
  getOptionLabel: (item: TItem) => string;
  getOptionValue: (item: TItem) => string;
  transformResponse: (response: TResponse) => TItem[];
}) => {
  return function ConfiguredAsyncAutoComplete(
    props: Omit<
      AsyncAutoCompleteProps<TItem, TResponse>,
      'getOptionLabel' | 'getOptionValue' | 'transformResponse'
    >,
  ) {
    return (
      <AsyncAutoComplete
        {...props}
        getOptionLabel={config.getOptionLabel}
        getOptionValue={config.getOptionValue}
        transformResponse={config.transformResponse}
      />
    );
  };
};
